// @react
import React, { useRef, useState } from 'react'
// @common
import { useHistory } from 'react-router-dom'
import colors from '@bit/atd.web.colors'
// @components
import { Tab, Tabs } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Container from '@bit/atd.web.container'
// @module components
import UserWelcomeMessage from '../UserWelcomeMessage'
import Helper from 'utils/Helper.class'
import { useLocation } from 'react-router-dom'
import Spacer from '@bit/atd.web.spacer'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_USER } from 'graphql/queries/user.query'
// @types
import { GetUserQuery, GetUserQueryVariables } from 'lib/graphql/graphqlTypes'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: colors.white,
  },
  header: {
    paddingTop: '1px',
    backgroundColor: '#fff',
    borderBottom: `1px solid ${colors.gray300}`,
  },
  tabsHeader: {
    '& .MuiTabs-indicator': {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      '& > span': {
        borderTopLeftRadius: '3px',
        borderTopRightRadius: '3px',
        backgroundColor: colors.ctaPrimary,
        width: 'calc(100% - 8px)',
      },
    },
  },
  tab: {
    marginRight: '8px',
    '&:hover': {
      color: colors.ctaPrimaryDark,
    },
    [theme.breakpoints.up('sm')]: {
      '&:focus': {
        outline: '-webkit-focus-ring-color auto 1px',
        outlineOffset: 0,
      },
    },
    [theme.breakpoints.up('sm')]: {
      marginRight: '12px',
    },
    '& .MuiTab-wrapper': {
      fontSize: '16px',
      lineHeight: '20px',
      paddingBottom: '4px',
      [theme.breakpoints.up('sm')]: {
        fontSize: '18px',
        lineHeight: '24px',
        paddingTop: '6px',
        paddingBottom: '8px',
      },
    },
  },
}))

type PropTypes = {
  children: React.ReactNode
}

const MyATDPageWrapper = (props: PropTypes) => {
  /******************************************
   * PROPS
   ******************************************/
  const history = useHistory()
  const { pathname } = useLocation()
  const { children } = props

  const { data } = useQuery<GetUserQuery, GetUserQueryVariables>(GET_USER, {
    fetchPolicy: 'cache-first',
  })

  const timeoutHistory = (key: string) => {
    setTimeout(() => {
      history.push(key)
    }, 300)
  }

  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  const tabIndexCalc = (): number => {
    switch (pathname) {
      case (pathname.match(/^\/myatd\/dashboard/) || {}).input:
        return 0
      case (pathname.match(/^\/myatd\/activity-history/) || {}).input:
        return 1
      case (pathname.match(/^\/myatd\/benefit-center/) || {}).input:
        return 2
      default:
        return 0
    }
  }
  const [value, setValue] = useState<number>(tabIndexCalc())

  /******************************************
   * FUNCTIONS
   ******************************************/

  const dashKey = Helper.getPathFromRouteKey('MYATD_DASHBOARD')
  const activityKey = Helper.getPathFromRouteKey(
    'MYATD_ACTIVITYHISTORY_LEARNING',
  )
  const benefitsKey = Helper.getPathFromRouteKey('MYATD_BENEFITS_CENTER')

  const handleChange = (
    event: React.ChangeEvent<Record<string, unknown>>,
    newValue: number,
  ) => {
    setValue(newValue)
  }

  /******************************************
   * RENDER
   ******************************************/

  const tabIndex = (
    <Container>
      <UserWelcomeMessage />
      <Tabs
        className={classes.tabsHeader}
        value={value}
        onChange={handleChange}
        aria-label="myATD-tab-headers"
        TabIndicatorProps={{ children: <span /> }}
      >
        <Tab
          className={classes.tab}
          value={0}
          tabIndex={0}
          component={'a'}
          onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            timeoutHistory(dashKey)
            event.preventDefault()
          }}
          label="Dashboard"
          href={dashKey}
          disableRipple
        />
        <Tab
          className={classes.tab}
          value={1}
          tabIndex={1}
          component={'a'}
          onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            timeoutHistory(activityKey)
            event.preventDefault()
          }}
          label="My Activity"
          href={activityKey}
          disableRipple
        />

        {data?.user?.membership?.isMember && (
          <Tab
            className={classes.tab}
            value={2}
            tabIndex={2}
            component={'a'}
            onClick={(
              event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
            ) => {
              timeoutHistory(benefitsKey)
              event.preventDefault()
            }}
            label="Benefits Center"
            href={benefitsKey}
            disableRipple
          />
        )}
      </Tabs>
    </Container>
  )

  return (
    <>
      <div className={classes.header}>{tabIndex}</div>
      <Spacer size={20} />
      {children}
    </>
  )
}
export default MyATDPageWrapper
